import type { QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  queryOptions,
  useMutation,
  useQueries,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'

import type { SessionSummary } from '../dal/session-dal'
import {
  abortSession,
  endSession,
  fetchSessionById,
  fetchSessionDefinition,
  fetchSessions,
  startSession,
} from '../dal/session-dal'
import { measurementQueryOptions } from './measurement-queries'
import { queryKeys } from './query-keys'

const PENDING_SESSION_DETAIL_REFETCH_INTERVAL_MS = 5000

type SessionDetail = Awaited<ReturnType<typeof fetchSessionById>>

interface UsePendingSessionAnalysisPollingOptions {
  sessions: readonly SessionSummary[] | undefined
  onSettled?: (session: SessionDetail) => void
}

// The backend only moves an analyzing session to a terminal result
// (`Completed` or `Aborted`). A `PendingAnalysis` -> `Active` "transition" can
// only come from a stale session-detail cache (e.g. the `Active` value fetched
// while the session was in progress) being read before its refetch resolves.
// Applying it would regress the list and is ignored.
function pendingSessionAnalysisSettled(
  session: SessionSummary,
  nextSession: SessionDetail,
) {
  return (
    session.status === 'PendingAnalysis' &&
    (nextSession.status === 'Completed' || nextSession.status === 'Aborted')
  )
}

// Updater must return the same session object when unchanged; reference
// identity is how we avoid replacing the list cache unnecessarily.
function updateSessionSummaries(
  queryClient: QueryClient,
  updateSession: (session: SessionSummary) => SessionSummary,
) {
  queryClient.setQueryData<SessionSummary[]>(
    queryKeys.backendSessions.all(),
    (currentSessions) => {
      if (currentSessions == null) {
        return currentSessions
      }

      let didUpdate = false
      const nextSessions = currentSessions.map((session) => {
        const updatedSession = updateSession(session)
        if (updatedSession !== session) {
          didUpdate = true
        }
        return updatedSession
      })

      return didUpdate ? nextSessions : currentSessions
    },
  )
}

export const backendSessionsQueryOptions = queryOptions({
  queryKey: queryKeys.backendSessions.all(),
  queryFn: fetchSessions,
  staleTime: 60 * 1000,
})

export const sessionDefinitionQueryOptions = (
  id: number,
  languageCode: string,
) =>
  queryOptions({
    queryKey: queryKeys.backendSessions.definition(id, languageCode),
    queryFn: () => fetchSessionDefinition(id, languageCode),
    staleTime: 5 * 60 * 1000,
  })

export const backendSessionQueryOptions = (id: number) =>
  queryOptions({
    queryKey: queryKeys.backendSessions.byId(id),
    queryFn: async ({ client }) => {
      const session = await fetchSessionById(id)
      const promises = session.measurements.map(({ id }) =>
        client.fetchQuery(measurementQueryOptions(id)),
      )

      const measurements = await Promise.all(promises)
      return {
        ...session,
        measurements: session.measurements.map((measurement) => ({
          ...measurement,
          hasRecording:
            measurements.find((m) => m.id === measurement.id)?.hasRecording ??
            false,
        })),
      }
    },
    staleTime: 60 * 1000,
  })

export function useStartSessionMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.mutations.sessionStarted,
    mutationFn: startSession,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.backendSessions.all(),
      })
    },
  })
}

function usePendingSessionAnalysisPolling({
  sessions,
  onSettled,
}: UsePendingSessionAnalysisPollingOptions) {
  const queryClient = useQueryClient()
  const notifiedSettledSessionIdsRef = useRef(new Set<number>())
  const pendingSessionIds = useMemo(
    () =>
      (sessions ?? [])
        .filter(
          (session, index) =>
            // We only want to continue polling the latest pending session
            session.status === 'PendingAnalysis' && index === 0,
        )
        .map((session) => session.id),
    [sessions],
  )

  const combinePendingSessionDetails = useCallback(
    (queries: { data: SessionDetail | undefined }[]) =>
      queries
        .map((query) => query.data)
        .filter((session): session is SessionDetail => session != null),
    [],
  )

  const pendingSessionDetails = useQueries({
    queries: pendingSessionIds.map((id) =>
      queryOptions({
        ...backendSessionQueryOptions(id),
        refetchInterval: (query) =>
          query.state.data?.status === 'PendingAnalysis'
            ? PENDING_SESSION_DETAIL_REFETCH_INTERVAL_MS
            : false,
      }),
    ),
    combine: combinePendingSessionDetails,
  })

  useEffect(() => {
    if (pendingSessionDetails.length === 0) {
      return
    }

    const polledSessionDetailsById = new Map(
      pendingSessionDetails.map((session) => [session.id, session]),
    )
    const settledSessions = (sessions ?? []).flatMap((session) => {
      const nextSession = polledSessionDetailsById.get(session.id)

      if (
        nextSession == null ||
        !pendingSessionAnalysisSettled(session, nextSession)
      ) {
        return []
      }

      return [nextSession]
    })

    if (settledSessions.length === 0) {
      return
    }

    updateSessionSummaries(queryClient, (session) => {
      return (
        settledSessions.find((nextSession) => nextSession.id === session.id) ??
        session
      )
    })

    settledSessions.forEach((session) => {
      if (notifiedSettledSessionIdsRef.current.has(session.id)) {
        return
      }

      notifiedSettledSessionIdsRef.current.add(session.id)
      onSettled?.(session)
    })
  }, [onSettled, pendingSessionDetails, queryClient, sessions])
}

export function useBackendSessionsQuery(options?: {
  onPendingSessionSettled?: (session: SessionDetail) => void
}) {
  const sessionsQuery = useSuspenseQuery(backendSessionsQueryOptions)

  usePendingSessionAnalysisPolling({
    sessions: sessionsQuery.data,
    onSettled: options?.onPendingSessionSettled,
  })

  return sessionsQuery
}

export function useAbortSessionMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.mutations.sessionAborted,
    mutationFn: abortSession,
    onSuccess: async (_data, sessionId) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.backendSessions.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.backendSessions.byId(sessionId),
        }),
      ])
    },
  })
}

export function useEndSessionMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.mutations.sessionEnded,
    mutationFn: endSession,
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.backendSessions.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.backendSessions.byId(variables.id),
        }),
      ])

      // The session list can briefly return the pre-End `Active` state while
      // the backend starts analysis. Keep the completed assessment from being
      // treated as resumable until a later fetch reports its terminal state.
      updateSessionSummaries(queryClient, (session) =>
        session.id === variables.id && session.status === 'Active'
          ? { ...session, status: 'PendingAnalysis' }
          : session,
      )
    },
  })
}
